/** The durable markup mutation log, apart from the preference capability, whose fallbacks and
 *  swallowed quota errors are no acknowledgement for user work. */
import type { Calibration, MarkupShape } from "./markupTypes";

const DB_NAME = "primepoint-markup-outbox";
const DB_VERSION = 2;
const OPERATIONS = "operations";
const SCOPES = "scopes";
const QUARANTINE = "legacy-quarantine";
const MAX_BYTES = 16 * 1024 * 1024;
const enqueueListeners = new Set<() => void>();

/** Wakes same-window replay only after a new durable row commits. */
export function subscribeMarkupOutboxEnqueue(listener: () => void): () => void {
    enqueueListeners.add(listener);
    return () => enqueueListeners.delete(listener);
}

function notifyMarkupOutboxEnqueue(): void {
    for (const listener of enqueueListeners) {
        try {
            listener();
        } catch {
            /* A wake observer cannot affect durable user work. */
        }
    }
}

export interface MarkupOutboxScope {
    /** Derived from verified auth, never a caller supplied organization slug. */
    origin: string;
    organizationId: string;
    memberId: string;
    fileId: string;
    page: number;
    revisionId: string | null;
    sectionGroupId?: string | null;
}

export type MarkupOutboxIdentity = Pick<
    MarkupOutboxScope,
    "origin" | "organizationId" | "memberId"
>;

export function markupOutboxScopeKey(scope: MarkupOutboxScope): string {
    return JSON.stringify([
        scope.origin,
        scope.organizationId,
        scope.memberId,
        scope.fileId,
        scope.page,
        scope.revisionId,
        ...(scope.sectionGroupId ? [scope.sectionGroupId] : []),
    ]);
}

export interface SaveOperationPayload {
    kind: "save";
    shapes: MarkupShape[];
    calibration: Calibration | null;
    expectedVersion: number | null;
    /** Required to distinguish an edit from a stale full snapshot on conflict. */
    baseline: { shapes: MarkupShape[]; calibration: Calibration | null } | null;
}

export interface ClearOperationPayload {
    kind: "clear";
}
export type MarkupOutboxPayload = SaveOperationPayload | ClearOperationPayload;
export type MarkupOutboxState = "pending" | "sending" | "conflict" | "failed";

export interface MarkupOutboxOperation {
    scopeKey: string;
    operationId: string;
    sequence: number;
    generation: number;
    payload: MarkupOutboxPayload;
    originalPayload?: MarkupOutboxPayload;
    state: MarkupOutboxState;
    attempts: number;
    nextAttemptAt: number;
    leaseOwner: string | null;
    leaseToken: string | null;
    leaseUntil: number | null;
    createdAt: number;
    updatedAt: number;
    bytes: number;
}

interface ScopeState {
    scopeKey: string;
    generation: number;
    /** Last server-confirmed generation; `generation` can be ahead after local Clears. */
    confirmedServerGeneration: number | null;
    /** A generation learned from the server may seed unknown offline work once. */
    generationKnown: boolean;
    /** Latches after a fenced server capability is confirmed; never regresses offline. */
    everFenced: boolean;
    /** Server's canonical identity for this scope. Never silently changes. */
    canonicalScopeId: string | null;
    nextSequence: number;
    clearBarrierSequence: number | null;
    bytes: number;
    foregroundOwner?: string | null;
    foregroundToken?: string | null;
    foregroundUntil?: number | null;
}

export interface MarkupOutboxForegroundLease {
    owner: string;
    token: string;
    until: number;
}

export interface MarkupOutboxScopeMetadata {
    canonicalScopeId: string;
    generation: number;
    fenced: boolean;
}

export type MarkupOutboxMetadataResult = "seeded" | "current" | "stale";

class MarkupOutboxStorageError extends Error {
    constructor(
        message: string,
        public readonly cause?: unknown,
    ) {
        super(message);
        this.name = "MarkupOutboxStorageError";
    }
}

function request<T>(value: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        value.onsuccess = () => resolve(value.result);
        value.onerror = () => reject(value.error);
    });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
    const completed = new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onabort = () => reject(transaction.error);
        transaction.onerror = () => reject(transaction.error);
    });
    // Observed at once: a failure can abort before a caller awaits `done`.
    void completed.catch(() => undefined);
    return completed;
}

let database: Promise<IDBDatabase> | null = null;
function openDatabase(): Promise<IDBDatabase> {
    if (typeof indexedDB === "undefined") {
        return Promise.reject(
            new MarkupOutboxStorageError("Offline markup storage is unavailable"),
        );
    }
    if (database) return database;
    database = new Promise((resolve, reject) => {
        const open = indexedDB.open(DB_NAME, DB_VERSION);
        let abandoned = false;
        open.onupgradeneeded = () => {
            const db = open.result;
            const operations = db.objectStoreNames.contains(OPERATIONS)
                ? open.transaction!.objectStore(OPERATIONS)
                : db.createObjectStore(OPERATIONS, { keyPath: ["scopeKey", "sequence"] });
            if (!operations.indexNames.contains("operationId"))
                operations.createIndex("operationId", "operationId", { unique: true });
            if (!db.objectStoreNames.contains(SCOPES))
                db.createObjectStore(SCOPES, { keyPath: "scopeKey" });
            if (!db.objectStoreNames.contains(QUARANTINE))
                db.createObjectStore(QUARANTINE, { keyPath: "id" });
        };
        open.onsuccess = () => {
            if (abandoned) {
                open.result.close();
                return;
            }
            open.result.onversionchange = () => {
                open.result.close();
                database = null;
            };
            resolve(open.result);
        };
        open.onerror = () => {
            database = null;
            reject(
                new MarkupOutboxStorageError("Could not open offline markup storage", open.error),
            );
        };
        open.onblocked = () => {
            abandoned = true;
            database = null;
            reject(
                new MarkupOutboxStorageError(
                    "Close older app windows to upgrade offline markup storage",
                ),
            );
        };
    });
    return database;
}

function operationId(): string {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function encodedBytes(value: unknown): number {
    return new Blob([JSON.stringify(value)]).size;
}

function newScopeState(scopeKey: string, generation = 0, generationKnown = false): ScopeState {
    return {
        scopeKey,
        generation,
        confirmedServerGeneration: null,
        generationKnown,
        everFenced: false,
        canonicalScopeId: null,
        nextSequence: 1,
        clearBarrierSequence: null,
        bytes: 0,
        foregroundOwner: null,
        foregroundToken: null,
        foregroundUntil: null,
    };
}

function normalizeScopeState(state: ScopeState): ScopeState {
    return {
        ...state,
        generationKnown: state.generationKnown ?? false,
        confirmedServerGeneration: state.confirmedServerGeneration ?? null,
        everFenced: state.everFenced ?? false,
        canonicalScopeId: state.canonicalScopeId ?? null,
        foregroundOwner: state.foregroundOwner ?? null,
        foregroundToken: state.foregroundToken ?? null,
        foregroundUntil: state.foregroundUntil ?? null,
    };
}

/** Allocates the per-scope sequence in the same transaction that writes the immutable row. */
export async function enqueueMarkupOutboxOperation(
    scope: MarkupOutboxScope,
    payload: MarkupOutboxPayload,
    initialGeneration?: number,
): Promise<MarkupOutboxOperation> {
    const db = await openDatabase();
    const scopeKey = markupOutboxScopeKey(scope);
    const transaction = db.transaction([SCOPES, OPERATIONS], "readwrite");
    const done = transactionDone(transaction);
    const scopes = transaction.objectStore(SCOPES);
    const operations = transaction.objectStore(OPERATIONS);
    try {
        const state = (await request(scopes.get(scopeKey))) as ScopeState | undefined;
        const current = state
            ? normalizeScopeState(state)
            : // Compatibility callers may still provide metadata's generation here, but it
              // is not authority until canonical server metadata is durably recorded.
              newScopeState(scopeKey, initialGeneration ?? 0);
        const bytes = encodedBytes({ payload, originalPayload: payload });
        if (current.bytes + bytes > MAX_BYTES) {
            transaction.abort();
            await done.catch(() => undefined);
            throw new MarkupOutboxStorageError(
                "Offline markup storage is full; your edit is still in memory",
            );
        }
        const now = Date.now();
        const row: MarkupOutboxOperation = {
            scopeKey,
            operationId: operationId(),
            sequence: current.nextSequence,
            generation: current.generation,
            payload,
            originalPayload: payload,
            state: "pending",
            attempts: 0,
            nextAttemptAt: now,
            leaseOwner: null,
            leaseToken: null,
            leaseUntil: null,
            createdAt: now,
            updatedAt: now,
            bytes,
        };
        current.nextSequence += 1;
        current.bytes += bytes;
        // A Clear is a durable ordered barrier. Saves after it are assigned the new
        // generation but are still retained as their own immutable intents.
        if (payload.kind === "clear") {
            current.clearBarrierSequence = row.sequence;
            current.generation += 1;
        }
        operations.add(row);
        scopes.put(current);
        await done;
        notifyMarkupOutboxEnqueue();
        return row;
    } catch (error) {
        if (error instanceof MarkupOutboxStorageError) throw error;
        throw new MarkupOutboxStorageError(
            "Could not save markup offline; your edit is still in memory",
            error,
        );
    }
}

/** Claims only the head operation. A later edit can never overtake an earlier Clear/save. */
export async function claimNextMarkupOutboxOperation(
    scope: MarkupOutboxScope,
    owner: string,
    leaseMs = 30_000,
    foregroundToken?: string,
): Promise<MarkupOutboxOperation | null> {
    const db = await openDatabase();
    const scopeKey = markupOutboxScopeKey(scope);
    const transaction = db.transaction([OPERATIONS, SCOPES], "readwrite");
    const done = transactionDone(transaction);
    const state = (await request(transaction.objectStore(SCOPES).get(scopeKey))) as
        ScopeState | undefined;
    if (
        state?.foregroundToken &&
        (state.foregroundUntil ?? 0) > Date.now() &&
        state.foregroundToken !== foregroundToken
    ) {
        await done;
        return null;
    }
    const store = transaction.objectStore(OPERATIONS);
    const range = IDBKeyRange.bound([scopeKey, 0], [scopeKey, Number.MAX_SAFE_INTEGER]);
    const rows = (await request(store.getAll(range))) as MarkupOutboxOperation[];
    const now = Date.now();
    // A scope is strictly ordered: inspect only its head. A failed/conflicting or
    // backoff-delayed head blocks all later saves and Clear barriers.
    const row = rows.sort((a, b) => a.sequence - b.sequence)[0];
    if (
        !row ||
        !(
            (row.state === "pending" && row.nextAttemptAt <= now) ||
            (row.state === "sending" && (row.leaseUntil ?? 0) <= now)
        )
    ) {
        await done;
        return null;
    }
    row.state = "sending";
    row.leaseOwner = owner;
    row.leaseToken = operationId();
    row.leaseUntil = now + leaseMs;
    row.updatedAt = now;
    store.put(row);
    await done;
    return row;
}

/** Publish owns this scope from its delivery barrier until the publish response settles.
 * Editing can still enqueue rows, but another window cannot dispatch them mid-publish. */
export async function claimMarkupOutboxForeground(
    scope: MarkupOutboxScope,
    owner: string,
    leaseMs = 30_000,
): Promise<MarkupOutboxForegroundLease | null> {
    const db = await openDatabase();
    const key = markupOutboxScopeKey(scope);
    const transaction = db.transaction(SCOPES, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(SCOPES);
    const state = (await request(store.get(key))) as ScopeState | undefined;
    const now = Date.now();
    if (state?.foregroundToken && (state.foregroundUntil ?? 0) > now) {
        await done;
        return null;
    }
    const lease = { owner, token: operationId(), until: now + leaseMs };
    store.put({
        ...(state ? normalizeScopeState(state) : newScopeState(key)),
        foregroundOwner: owner,
        foregroundToken: lease.token,
        foregroundUntil: lease.until,
    });
    await done;
    return lease;
}

export async function renewMarkupOutboxForeground(
    scope: MarkupOutboxScope,
    lease: MarkupOutboxForegroundLease,
    leaseMs = 30_000,
): Promise<MarkupOutboxForegroundLease | null> {
    const db = await openDatabase();
    const key = markupOutboxScopeKey(scope);
    const transaction = db.transaction(SCOPES, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(SCOPES);
    const state = (await request(store.get(key))) as ScopeState | undefined;
    if (
        !state ||
        state.foregroundOwner !== lease.owner ||
        state.foregroundToken !== lease.token ||
        (state.foregroundUntil ?? 0) <= Date.now()
    ) {
        await done;
        return null;
    }
    const renewed = { ...lease, until: Date.now() + leaseMs };
    store.put({ ...normalizeScopeState(state), foregroundUntil: renewed.until });
    await done;
    return renewed;
}

export async function releaseMarkupOutboxForeground(
    scope: MarkupOutboxScope,
    lease: MarkupOutboxForegroundLease,
): Promise<boolean> {
    const db = await openDatabase();
    const key = markupOutboxScopeKey(scope);
    const transaction = db.transaction(SCOPES, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(SCOPES);
    const state = (await request(store.get(key))) as ScopeState | undefined;
    if (!state || state.foregroundOwner !== lease.owner || state.foregroundToken !== lease.token) {
        await done;
        return false;
    }
    store.put({
        ...normalizeScopeState(state),
        foregroundOwner: null,
        foregroundToken: null,
        foregroundUntil: null,
    });
    await done;
    return true;
}

/** Records server authority apart from local intent: the first verified metadata may seed
 *  offline rows; a later mismatch surfaces for recovery, never rewritten across a Clear. */
export async function recordMarkupOutboxScopeMetadata(
    scope: MarkupOutboxScope,
    metadata: MarkupOutboxScopeMetadata,
): Promise<MarkupOutboxMetadataResult> {
    const db = await openDatabase();
    const scopeKey = markupOutboxScopeKey(scope);
    const transaction = db.transaction([SCOPES, OPERATIONS], "readwrite");
    const done = transactionDone(transaction);
    const scopes = transaction.objectStore(SCOPES);
    const operations = transaction.objectStore(OPERATIONS);
    const found = (await request(scopes.get(scopeKey))) as ScopeState | undefined;
    const state = found ? normalizeScopeState(found) : newScopeState(scopeKey);
    const rows = (
        (await request(
            operations.getAll(
                IDBKeyRange.bound([scopeKey, 0], [scopeKey, Number.MAX_SAFE_INTEGER]),
            ),
        )) as MarkupOutboxOperation[]
    ).sort((a, b) => a.sequence - b.sequence);
    // A legacy in-flight row stays: a new generation would void its acknowledgement.
    if (rows.some((row) => row.state === "sending")) {
        await done;
        return "current";
    }
    if (state.generationKnown) {
        if (state.canonicalScopeId !== metadata.canonicalScopeId) {
            await done;
            return "stale";
        }
        // The receipt and idempotency decide delivery; metadata never blocks replay.
        if (rows.length === 0) state.generation = metadata.generation;
        state.confirmedServerGeneration = metadata.generation;
        state.everFenced ||= metadata.fenced;
        state.canonicalScopeId = metadata.canonicalScopeId;
        scopes.put(state);
        await done;
        return "current";
    }
    // Unknown rows cannot include a confirmed Clear: set every queued row to the one
    // verified starting generation, preserving their relative Clear barrier increments.
    let generation = metadata.generation;
    for (const row of rows) {
        row.generation = generation;
        if (row.payload.kind === "clear") generation += 1;
        operations.put(row);
    }
    state.generation = generation;
    state.confirmedServerGeneration = metadata.generation;
    state.generationKnown = true;
    state.everFenced = metadata.fenced;
    state.canonicalScopeId = metadata.canonicalScopeId;
    scopes.put(state);
    await done;
    return "seeded";
}

/** Exact-operation conditional acknowledgement; an old response cannot remove a newer row. */
export async function acknowledgeMarkupOutboxOperation(
    operation: Pick<
        MarkupOutboxOperation,
        "scopeKey" | "sequence" | "operationId" | "generation" | "leaseToken"
    >,
    owner: string,
): Promise<boolean> {
    const db = await openDatabase();
    const transaction = db.transaction([SCOPES, OPERATIONS], "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(OPERATIONS);
    const row = (await request(store.get([operation.scopeKey, operation.sequence]))) as
        MarkupOutboxOperation | undefined;
    if (
        !row ||
        row.operationId !== operation.operationId ||
        row.generation !== operation.generation ||
        row.leaseOwner !== owner ||
        row.leaseToken !== operation.leaseToken
    ) {
        await done;
        return false;
    }
    store.delete([row.scopeKey, row.sequence]);
    const scopes = transaction.objectStore(SCOPES);
    const state = (await request(scopes.get(row.scopeKey))) as ScopeState | undefined;
    if (state)
        scopes.put({ ...normalizeScopeState(state), bytes: Math.max(0, state.bytes - row.bytes) });
    await done;
    return true;
}

export async function markMarkupOutboxOperation(
    operation: Pick<MarkupOutboxOperation, "scopeKey" | "sequence" | "operationId" | "leaseToken">,
    owner: string,
    state: "pending" | "conflict" | "failed",
    nextAttemptAt = Date.now(),
): Promise<boolean> {
    const db = await openDatabase();
    const transaction = db.transaction(OPERATIONS, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(OPERATIONS);
    const row = (await request(store.get([operation.scopeKey, operation.sequence]))) as
        MarkupOutboxOperation | undefined;
    if (
        !row ||
        row.operationId !== operation.operationId ||
        row.leaseOwner !== owner ||
        row.leaseToken !== operation.leaseToken
    ) {
        await done;
        return false;
    }
    row.state = state;
    row.attempts += 1;
    row.nextAttemptAt = nextAttemptAt;
    row.leaseOwner = null;
    row.leaseToken = null;
    row.leaseUntil = null;
    row.updatedAt = Date.now();
    store.put(row);
    await done;
    return true;
}

/** Rebase is an in-place transactional retry of the same ordered head. The original
 * intent is retained for recovery; no ack/delete/enqueue gap can cross a Clear. */
export async function rebaseClaimedMarkupOutboxOperation(
    operation: MarkupOutboxOperation,
    owner: string,
    payload: SaveOperationPayload,
): Promise<boolean> {
    const db = await openDatabase();
    const transaction = db.transaction([SCOPES, OPERATIONS], "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(OPERATIONS);
    const row = (await request(store.get([operation.scopeKey, operation.sequence]))) as
        MarkupOutboxOperation | undefined;
    if (
        !row ||
        row.operationId !== operation.operationId ||
        row.leaseOwner !== owner ||
        row.leaseToken !== operation.leaseToken
    ) {
        await done;
        return false;
    }
    const scopes = transaction.objectStore(SCOPES);
    const state = (await request(scopes.get(operation.scopeKey))) as ScopeState | undefined;
    const originalPayload = row.originalPayload ?? row.payload;
    const bytes = encodedBytes({ payload, originalPayload });
    const oldBytes = row.bytes;
    if (!state || state.bytes - oldBytes + bytes > MAX_BYTES) {
        transaction.abort();
        await done.catch(() => undefined);
        throw new MarkupOutboxStorageError(
            "Offline markup storage is full; cannot rebase this edit",
        );
    }
    row.payload = payload;
    row.originalPayload = originalPayload;
    row.bytes = bytes;
    row.attempts += 1;
    row.state = "pending";
    row.leaseOwner = null;
    row.leaseToken = null;
    row.leaseUntil = null;
    row.updatedAt = Date.now();
    store.put(row);
    scopes.put({ ...normalizeScopeState(state), bytes: state.bytes - oldBytes + bytes });
    await done;
    return true;
}

export async function markupOutboxDiagnostics(
    scope: MarkupOutboxScope,
): Promise<{ count: number; bytes: number }> {
    const db = await openDatabase();
    const scopeKey = markupOutboxScopeKey(scope);
    const transaction = db.transaction([SCOPES, OPERATIONS], "readonly");
    const done = transactionDone(transaction);
    const state = (await request(transaction.objectStore(SCOPES).get(scopeKey))) as
        ScopeState | undefined;
    const count = (await request(
        transaction
            .objectStore(OPERATIONS)
            .count(IDBKeyRange.bound([scopeKey, 0], [scopeKey, Number.MAX_SAFE_INTEGER])),
    )) as number;
    await done;
    return { count, bytes: state?.bytes ?? 0 };
}

/** Once a verified fenced capability has been seen, a later metadata outage must
 * not silently downgrade replay to an unfenced request. */
export async function hasMarkupOutboxEverFenced(scope: MarkupOutboxScope): Promise<boolean> {
    const db = await openDatabase();
    const transaction = db.transaction(SCOPES, "readonly");
    const done = transactionDone(transaction);
    const state = (await request(
        transaction.objectStore(SCOPES).get(markupOutboxScopeKey(scope)),
    )) as ScopeState | undefined;
    await done;
    return state ? normalizeScopeState(state).everFenced : false;
}

/** Snapshot for restart/recovery UI. Rows remain ordered and include conflict/failed
 * heads; consumers must not infer a count from historical sequence allocation. */
export async function readMarkupOutboxPendingOperations(
    scope: MarkupOutboxScope,
): Promise<MarkupOutboxOperation[]> {
    const db = await openDatabase();
    const key = markupOutboxScopeKey(scope);
    const transaction = db.transaction(OPERATIONS, "readonly");
    const done = transactionDone(transaction);
    const rows = (await request(
        transaction
            .objectStore(OPERATIONS)
            .getAll(IDBKeyRange.bound([key, 0], [key, Number.MAX_SAFE_INTEGER])),
    )) as MarkupOutboxOperation[];
    await done;
    return rows.sort((a, b) => a.sequence - b.sequence);
}

/** Lists only durable scopes owned by the verified identity currently using this window.
 * Scope keys are private IndexedDB implementation detail; reject malformed legacy data. */
export async function listMarkupOutboxScopesForIdentity(
    identity: MarkupOutboxIdentity,
): Promise<MarkupOutboxScope[]> {
    const db = await openDatabase();
    const transaction = db.transaction(SCOPES, "readonly");
    const done = transactionDone(transaction);
    const states = (await request(transaction.objectStore(SCOPES).getAll())) as ScopeState[];
    await done;
    return states.flatMap(({ scopeKey }) => {
        try {
            const value = JSON.parse(scopeKey);
            if (!Array.isArray(value) || (value.length !== 6 && value.length !== 7)) return [];
            const [origin, organizationId, memberId, fileId, page, revisionId, sectionGroupId] =
                value;
            if (
                origin !== identity.origin ||
                organizationId !== identity.organizationId ||
                memberId !== identity.memberId ||
                typeof fileId !== "string" ||
                typeof page !== "number" ||
                (typeof revisionId !== "string" && revisionId !== null) ||
                (sectionGroupId !== undefined &&
                    (typeof sectionGroupId !== "string" || !sectionGroupId))
            )
                return [];
            return [
                {
                    origin,
                    organizationId,
                    memberId,
                    fileId,
                    page,
                    revisionId,
                    ...(sectionGroupId ? { sectionGroupId } : {}),
                },
            ];
        } catch {
            return [];
        }
    });
}

/** Re-arms only the current terminal head. A recovery UI can retry a retained
 * conflict/failed snapshot without minting a second operation behind its barrier. */
export async function retryMarkupOutboxOperation(
    scope: MarkupOutboxScope,
    operationIdToRetry: string,
): Promise<boolean> {
    const db = await openDatabase();
    const key = markupOutboxScopeKey(scope);
    const transaction = db.transaction(OPERATIONS, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(OPERATIONS);
    const rows = (
        (await request(
            store.getAll(IDBKeyRange.bound([key, 0], [key, Number.MAX_SAFE_INTEGER])),
        )) as MarkupOutboxOperation[]
    ).sort((a, b) => a.sequence - b.sequence);
    const row = rows[0];
    if (
        !row ||
        row.operationId !== operationIdToRetry ||
        (row.state !== "conflict" && row.state !== "failed" && row.state !== "pending")
    ) {
        await done;
        return false;
    }
    row.state = "pending";
    row.nextAttemptAt = Date.now();
    row.leaseOwner = null;
    row.leaseToken = null;
    row.leaseUntil = null;
    row.updatedAt = Date.now();
    store.put(row);
    await done;
    return true;
}

/** A foreground Publish captures a sequence and waits for this server-delivery
 * barrier. Local IndexedDB commit alone is deliberately insufficient. */
export async function isMarkupOutboxDeliveredThrough(
    scope: MarkupOutboxScope,
    sequence: number,
): Promise<boolean> {
    const db = await openDatabase();
    const key = markupOutboxScopeKey(scope);
    const transaction = db.transaction(OPERATIONS, "readonly");
    const done = transactionDone(transaction);
    const rows = (await request(
        transaction.objectStore(OPERATIONS).getAll(IDBKeyRange.bound([key, 0], [key, sequence])),
    )) as MarkupOutboxOperation[];
    await done;
    return rows.length === 0;
}

export async function latestMarkupOutboxSequence(scope: MarkupOutboxScope): Promise<number> {
    const db = await openDatabase();
    const transaction = db.transaction(SCOPES, "readonly");
    const done = transactionDone(transaction);
    const state = (await request(
        transaction.objectStore(SCOPES).get(markupOutboxScopeKey(scope)),
    )) as ScopeState | undefined;
    await done;
    return state ? state.nextSequence - 1 : 0;
}

/** V1 queue rows were scoped only to an organization. Preserve them for recovery,
 * but never attach private work to whichever member logs in next or replay them. */
export async function quarantineAmbiguousLegacyMarkupQueue(
    legacyStorageKey: string,
    entries: unknown,
): Promise<void> {
    const db = await openDatabase();
    const transaction = db.transaction(QUARANTINE, "readwrite");
    const done = transactionDone(transaction);
    const store = transaction.objectStore(QUARANTINE);
    const id = `v1:${legacyStorageKey}`;
    if (!(await request(store.get(id)))) {
        store.add({ id, legacyStorageKey, entries, quarantinedAt: Date.now() });
    }
    await done;
}
